import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Check, TrendingUp, Globe2, KeyRound } from "lucide-react";
import { IMAGES, SERVICES, STATS, DESTINATIONS } from "@/data/content";
import SplitText from "@/components/SplitText";
import MagneticButton from "@/components/MagneticButton";
import ImageReveal from "@/components/ImageReveal";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

const CHAPTER_IMAGES = [IMAGES.interiorEditorial, IMAGES.condoPool, IMAGES.villaAlt];
const CHAPTER_ICONS = [TrendingUp, Globe2, KeyRound];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page" className="overflow-x-clip">
      {/* ===================== HERO ===================== */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-end overflow-hidden hero-spotlight"
      >
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={IMAGES.heroVilla}
            alt="Luxury villa at twilight"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gl-bg via-gl-bg/55 to-gl-bg/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-gl-bg/80 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gl-primary/15 mix-blend-multiply" />
        </motion.div>

        {/* top eyebrow line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="absolute top-28 md:top-32 left-0 right-0"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-4">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="h-px w-16 bg-gl-gold origin-left"
            />
            <span className="text-gl-gold uppercase tracking-[0.35em] text-[11px] md:text-xs">
              International Real Estate Investment Consulting
            </span>
          </div>
        </motion.div>

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-24 md:pb-32"
        >
          <SplitText
            as="h1"
            className="font-serif-display font-light leading-[0.92] text-gl-text text-[3.4rem] sm:text-7xl lg:text-[8.5rem]"
            lines={[
              "Your Global",
              "Property Goals,",
              <span key="g" className="italic text-gl-gold">Strategically Funded.</span>,
            ]}
            delay={0.5}
            stagger={0.13}
          />

          <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.9 }}
              className="text-lg md:text-xl text-gl-text2 font-light leading-relaxed max-w-xl"
            >
              Master the financial habits and creative financing strategies needed
              to invest in international real estate with confidence.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.9 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-5 flex-shrink-0"
            >
              <MagneticButton
                to="/contact"
                data-testid="hero-cta-button"
                className="group px-9 py-4 bg-gl-gold text-gl-bg font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300"
              >
                Schedule Your Strategy Call
                <ArrowRight size={18} />
              </MagneticButton>
              <Link
                to="/services"
                data-testid="hero-secondary-button"
                className="text-gl-text border-b border-gl-gold/40 pb-1 hover:border-gl-gold hover:text-gl-gold transition-colors duration-300"
              >
                Explore the Framework
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-12 flex items-center gap-3 text-gl-text2"
        >
          <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <ArrowDown size={16} className="text-gl-gold" />
          </motion.span>
        </motion.div>
      </section>

      {/* ===================== MARQUEE ===================== */}
      <section className="bg-gl-bg2 border-y border-gl-gold/15 py-10 md:py-14">
        <Marquee
          items={DESTINATIONS}
          duration={40}
          className="font-serif-display text-4xl md:text-6xl text-gl-text font-light"
          itemClassName="italic"
        />
      </section>

      {/* ===================== MANIFESTO / PIVOT ===================== */}
      <section className="py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-baseline gap-5 mb-8">
                  <span className="font-serif-display text-2xl text-gl-gold">01</span>
                  <span className="h-px flex-1 bg-gl-gold/25" />
                  <span className="text-gl-text2 uppercase tracking-[0.3em] text-xs">The Reframe</span>
                </div>
              </Reveal>
              <SplitText
                as="h2"
                animateOnLoad={false}
                className="font-serif-display font-light leading-[1.02] text-gl-text text-4xl md:text-6xl"
                lines={[
                  "The barrier is",
                  <span key="a">rarely <span className="italic text-gl-gold">capital.</span></span>,
                  "It's structure.",
                ]}
              />
              <Reveal delay={0.15}>
                <p className="mt-9 text-gl-text2 text-lg font-light leading-relaxed max-w-xl">
                  Most people assume owning property abroad requires a fortune sitting
                  in the bank. We think differently. By building elite wealth habits and
                  deploying creative financing — leveraging existing assets, cross-border
                  loans, and strategic partnerships — we unlock markets you assumed were
                  out of reach.
                </p>
              </Reveal>
              <div className="mt-10 space-y-4">
                {[
                  "Reframe what's actually possible with your position",
                  "Replace guesswork with a repeatable acquisition system",
                  "Move with the credibility of a seasoned global investor",
                ].map((t, i) => (
                  <Reveal key={t} delay={0.2 + i * 0.08}>
                    <div className="flex items-start gap-4">
                      <span className="mt-1 w-5 h-5 flex-shrink-0 border border-gl-gold/50 flex items-center justify-center">
                        <Check size={12} className="text-gl-gold" />
                      </span>
                      <span className="text-gl-text2 font-light">{t}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <ImageReveal
                  src={IMAGES.interiorWarm}
                  alt="Editorial luxury interior"
                  className="aspect-[3/4] w-full"
                />
                <Reveal delay={0.3}>
                  <div className="absolute -bottom-8 -left-6 md:-left-10 bg-gl-gold text-gl-bg p-8 max-w-[240px]">
                    <div className="font-serif-display text-4xl leading-none">Habits</div>
                    <div className="font-serif-display text-4xl italic leading-none">first.</div>
                    <p className="text-sm mt-3 leading-snug">
                      Strategy compounds. So does discipline.
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FRAMEWORK CHAPTERS ===================== */}
      <section className="bg-gl-bg2/40 py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-baseline gap-5 mb-16 md:mb-24">
            <span className="font-serif-display text-2xl text-gl-gold">02</span>
            <span className="h-px flex-1 bg-gl-gold/25" />
            <span className="text-gl-text2 uppercase tracking-[0.3em] text-xs">The Framework</span>
          </div>

          <div className="space-y-24 md:space-y-40">
            {SERVICES.map((s, i) => {
              const reversed = i % 2 === 1;
              const Icon = CHAPTER_ICONS[i];
              return (
                <div
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                >
                  <div className={reversed ? "lg:order-2" : ""}>
                    <ImageReveal
                      src={CHAPTER_IMAGES[i]}
                      alt={s.title}
                      className="aspect-[4/3] w-full"
                    />
                  </div>
                  <div className={reversed ? "lg:order-1" : ""}>
                    <Reveal>
                      <div className="flex items-center gap-4 mb-6">
                        <span className="w-12 h-12 border border-gl-gold/40 flex items-center justify-center text-gl-gold">
                          <Icon size={20} />
                        </span>
                        <span className="font-serif-display text-7xl md:text-8xl text-stroke-gold leading-none">
                          {s.id}
                        </span>
                      </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                      <h3 className="font-serif-display text-3xl md:text-5xl font-light text-gl-text mb-4">
                        {s.title}
                      </h3>
                      <p className="text-gl-gold/90 font-light mb-5">{s.tagline}</p>
                      <p className="text-gl-text2 text-lg font-light leading-relaxed max-w-lg">
                        {s.description}
                      </p>
                    </Reveal>
                    <div className="mt-7 space-y-3">
                      {s.points.map((p, j) => (
                        <Reveal key={p} delay={0.15 + j * 0.06}>
                          <div className="flex items-center gap-3">
                            <span className="w-1.5 h-1.5 bg-gl-gold rotate-45" />
                            <span className="text-gl-text2 font-light">{p}</span>
                          </div>
                        </Reveal>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== SPOTLIGHT ===================== */}
      <SpotlightSection />

      {/* ===================== STATS ===================== */}
      <section className="py-20 md:py-28 border-y border-gl-gold/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="font-serif-display text-5xl md:text-6xl text-gl-gold">{s.value}</div>
              <div className="mt-3 text-sm text-gl-text2 tracking-wide">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <CtaSection />
    </div>
  );
}

function SpotlightSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1.3]);

  return (
    <section ref={ref} className="relative h-[70vh] md:h-[85vh] overflow-hidden hero-spotlight">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroDusk} alt="Coastal luxury property" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gl-bg/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-gl-bg via-transparent to-gl-bg/40" />
      </motion.div>
      <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <SplitText
          as="h2"
          animateOnLoad={false}
          className="font-serif-display font-light leading-[1.02] text-gl-text text-4xl md:text-7xl max-w-4xl"
          lines={[
            "We don't just find",
            <span key="p">properties. We build the <span className="italic text-gl-gold">position.</span></span>,
          ]}
        />
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="relative py-32 md:py-48 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.coastalAerial} alt="Global coastline" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gl-bg/88" />
        <div className="absolute inset-0 bg-gl-primary/25 mix-blend-multiply" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
        <SplitText
          as="h2"
          animateOnLoad={false}
          className="font-serif-display text-4xl md:text-7xl font-light text-gl-text leading-[1.03]"
          lines={["Ready to Map Your", <span key="i" className="italic text-gl-gold">International Move?</span>]}
        />
        <Reveal delay={0.2}>
          <p className="mt-8 text-lg text-gl-text2 font-light max-w-2xl mx-auto">
            Select a time to secure your 1:1 strategy consultation. We'll
            pressure-test your goals and outline the exact path forward.
          </p>
        </Reveal>
        <Reveal delay={0.32}>
          <div className="mt-12 flex justify-center">
            <MagneticButton
              to="/contact"
              data-testid="cta-band-button"
              className="px-10 py-5 bg-gl-gold text-gl-bg font-medium tracking-wide text-lg hover:bg-gl-goldHover transition-colors duration-300"
            >
              Schedule Your Strategy Call
              <ArrowRight size={20} />
            </MagneticButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
